import { useState, useEffect, useCallback, useMemo } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { calcCompleteness } from '@/utils/resumeScoring';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const EMPTY_PERSONAL = { name: '', email: '', phone: '', linkedin: '', location: '' };
const EMPTY_SKILLS = { technical: [], soft: [], languages: [] };

function applyResumeData(data, setters) {
  setters.setTitle(data.title);
  setters.setTemplate(data.template);
  setters.setPersonalInfo(data.data.personal_info || EMPTY_PERSONAL);
  setters.setProfessionalSummary(data.data.professional_summary || '');
  setters.setWorkExperience(data.data.work_experience || []);
  setters.setEducation(data.data.education || []);
  setters.setSkills(data.data.skills || EMPTY_SKILLS);
  setters.setProjects(data.data.projects || []);
  setters.setCertifications(data.data.certifications || []);
  setters.setShareToken(data.share_token || null);
  setters.setDataLoaded(true);
}

export function useResumeData(id, isNew, navigate) {
  const [title, setTitle] = useState('My Resume');
  const [template, setTemplate] = useState('modern');
  const [saving, setSaving] = useState(false);
  const [resumeId, setResumeId] = useState(isNew ? null : id);
  const [dataLoaded, setDataLoaded] = useState(false);
  const [personalInfo, setPersonalInfo] = useState(EMPTY_PERSONAL);
  const [professionalSummary, setProfessionalSummary] = useState('');
  const [workExperience, setWorkExperience] = useState([]);
  const [education, setEducation] = useState([]);
  const [skills, setSkills] = useState(EMPTY_SKILLS);
  const [projects, setProjects] = useState([]);
  const [certifications, setCertifications] = useState([]);
  const [shareToken, setShareToken] = useState(null);

  const setters = useMemo(() => ({
    setTitle, setTemplate, setPersonalInfo, setProfessionalSummary,
    setWorkExperience, setEducation, setSkills, setProjects,
    setCertifications, setShareToken, setDataLoaded,
  }), []);

  useEffect(() => {
    if (isNew) { setDataLoaded(true); return; }
    let cancelled = false;
    (async () => {
      try {
        const { data } = await axios.get(`${API_URL}/api/resumes/${id}`, { withCredentials: true });
        if (!cancelled) applyResumeData(data, setters);
      } catch (error) {
        toast.error('Failed to load resume');
      }
    })();
    return () => { cancelled = true; };
  }, [id, isNew, setters]);

  const buildResumePayload = useCallback(() => ({
    title, template,
    data: { personal_info: personalInfo, professional_summary: professionalSummary, work_experience: workExperience, education, skills, projects, certifications }
  }), [title, template, personalInfo, professionalSummary, workExperience, education, skills, projects, certifications]);

  const saveResume = useCallback(async () => {
    setSaving(true);
    try {
      const payload = buildResumePayload();
      if (resumeId) {
        await axios.put(`${API_URL}/api/resumes/${resumeId}`, payload, { withCredentials: true });
        toast.success('Resume saved successfully');
      } else {
        const { data } = await axios.post(`${API_URL}/api/resumes`, payload, { withCredentials: true });
        setResumeId(data.id);
        navigate(`/builder/${data.id}`, { replace: true });
        toast.success('Resume created successfully');
      }
    } catch (error) {
      toast.error('Failed to save resume');
    } finally {
      setSaving(false);
    }
  }, [resumeId, buildResumePayload, navigate]);

  const completenessScore = useCallback(
    () => calcCompleteness({ personalInfo, professionalSummary, workExperience, education, skills, projects, certifications }),
    [personalInfo, professionalSummary, workExperience, education, skills, projects, certifications]
  );

  return {
    title, setTitle, template, setTemplate, saving, resumeId, dataLoaded,
    personalInfo, setPersonalInfo, professionalSummary, setProfessionalSummary,
    workExperience, setWorkExperience, education, setEducation,
    skills, setSkills, projects, setProjects, certifications, setCertifications,
    buildResumePayload, saveResume, completenessScore, shareToken,
  };
}
